/**
 * DEFUSE S10 — per-instance child IDENTITY: the synthetic id / spaceId /
 * effective-layer record a flattened child carries, the AutoCAD layer-0
 * inheritance rule, and the annotative-context transform that rides with it.
 *
 * Split out of `./blockTransform.ts` (which re-exports the public surface and
 * carries the family's module documentation) purely for file size — the rules
 * are unchanged. Depends on `./blockInsertTransform.ts` and (for the
 * annotative rotation map) `./blockArcEllipseMapping.ts`.
 */

import type {
  AnnotScaleContext,
  BlockDefinition,
} from "@dxf-ai-editor/drawing-model";

import type { Vec3 } from "../../types";

import type { AdapterContext } from "../adapterContext";

import {
  transformBlockChildPoint,
  type BlockInsertTransform,
} from "./blockInsertTransform";
// The annotative-context transform maps a context's own rotation through the
// SAME direction table the hatch pattern / MTEXT direction paths use.
import { mapDirectionAngleDeg } from "./blockArcEllipseMapping";

/** Per-instance identity assigned to a flattened child renderable —
 *  replicates the legacy synthetic id (`blockInsert.ts:351`)
 *  `${parentInsertEntityId}::${child.id}-${childIndex}` and the parent
 *  INSERT's spaceId. */
export interface BlockChildIdentity {
  id: string;
  spaceId: string;
  /** EFFECTIVE layer of the enclosing INSERT chain (@codex #431 round-3):
   *  a child authored on layer "0" inherits it (AutoCAD block-layer
   *  semantics); through nested INSERTs the innermost non-"0" INSERT layer
   *  wins (a nested INSERT on "0" defers up the chain). */
  parentLayer: string;
}

/**
 * AutoCAD block-child layer resolution (@codex #431 round-3): a child
 * authored on layer "0" inherits the enclosing INSERT's effective layer;
 * any other layer is kept verbatim. Typed twin of the repo's canonical rule
 * — `state/blockReducerHelpers.ts resolveChildLayer` (EXPLODE reducer) and
 * `print/pdf/converters/insertConverter.ts resolveChildLayer` (PDF-6.2a) —
 * both private to their modules, hence this local one-liner with the same
 * semantics (exact `"0"` match). The legacy scene3d flatten does NOT apply
 * it (`blockChildToEntityPreview` writes `child.layer ?? "0"` verbatim) —
 * a legacy-wrong carve-out like the Point/BYBLOCK-colour fixes; golden
 * fixtures avoid layer-0 children under non-"0" INSERTs.
 */
export function resolveBlockChildLayer(
  childLayer: string,
  parentLayer: string,
): string {
  return childLayer === "0" ? parentLayer : childLayer;
}

/**
 * The per-instance re-homing every COMPOSITE block-child arm does before it
 * runs a production adapter on the child in its OWN (block-local) frame —
 * `./blockChildMultileader.ts` (MLEADER) and `./blockChildTable.ts`
 * (ACAD_TABLE), @codex PR #785 round 1 F1.
 *
 * Those arms deliberately do NOT clone the child into world space (their
 * geometry is derived from scalars a point transform cannot carry, or — for a
 * table — from a nested block reference the entity has no scale field for), so
 * the ONLY rewrite they need is the non-geometric half of
 * `transformBlockChild`'s `resolved` spread: the synthetic per-instance
 * identity and the AutoCAD layer-"0" inheritance. Every coordinate and every
 * scalar stays AUTHORED — that is what makes the arms' own placement the
 * single, exact transform.
 *
 * `adapterCtx` additionally threads the enclosing flatten's block table onto
 * the context (reusing the SAME object when it is already there, so no
 * needless context identity churn) — a render branch that passes its block
 * definitions explicitly may leave `ctx.blocks` unset, and without this the
 * child's own nested content would silently vanish on that branch only.
 *
 * ONE definition for both arms: jscpd flagged the second copy the moment the
 * table arm landed, and a drifting copy here would mean a table and an MLEADER
 * inheriting DIFFERENT layers from the same INSERT.
 */
export function bakeBlockChildLocal<
  T extends { id: string; spaceId: string; layer: string },
>(
  child: T,
  identity: BlockChildIdentity,
  ctx: AdapterContext,
  blocks: Record<string, BlockDefinition>,
): { local: T; adapterCtx: AdapterContext } {
  return {
    local: {
      ...child,
      id: identity.id,
      spaceId: identity.spaceId,
      layer: resolveBlockChildLayer(child.layer, identity.parentLayer),
    },
    adapterCtx: ctx.blocks === blocks ? ctx : { ...ctx, blocks },
  };
}

/**
 * S10d (round-18 FINDING — textFromEntity:83 / hatchFromEntity block path) —
 * transform a block-child's per-scale `annotativeContexts` from the block's
 * LOCAL frame into WORLD space, so the leaf adapter's active-scale override
 * (`resolveTypedAnnotation` → `anno.insertion` / `anno.rotation` / hatch
 * `patternAngle`/`patternScale`) lands in the SAME world frame the child's
 * transformed master fields do.
 *
 * Why this is needed: `transformBlockChild` converts a child's master
 * `insertionPoint` to world coordinates, but the raw `...child` spread left
 * `annotativeContexts` in block-LOCAL coordinates. When a context matched the
 * active CANNOSCALE, `textFromEntity` replaced the world-transformed insertion
 * with the raw block-local context point (label snapped near the drawing
 * origin instead of inside the block); `hatchFromEntity` applied a pattern
 * angle that ignored the INSERT rotation/scale.
 *
 * The conventions mirror each sibling master field EXACTLY:
 *   - insertion / alignment points → `transformBlockChildPoint` (same as the
 *     master `insertionPoint` / `alignmentPoint`);
 *   - absolute rotation → `+ scalarRotationDeg` (same fold as the master
 *     `rotation` sum, incl. the double-negative 180° case);
 *   - hatch `patternAngle` → `mapDirectionAngleDeg`, `patternScale` →
 *     `× |scaleX|` (same as the master pattern fields).
 * Context kinds without a positional field (dimension/mleader/leader — never
 * present on a TEXT/MTEXT/HATCH child) pass through untouched.
 *
 * Returns `undefined` for an empty list (the common non-annotative child) so
 * the caller keeps the original array reference — a no-op on the hot flatten
 * path and byte-identical golden-parity output.
 */
export function transformChildAnnotativeContexts(
  contexts: ReadonlyArray<AnnotScaleContext>,
  basePoint: Vec3,
  t: BlockInsertTransform,
  scalarRotationDeg: number,
  radiusScale: number,
): AnnotScaleContext[] | undefined {
  if (contexts.length === 0) return undefined;
  const pt2 = (p: { x: number; y: number }): { x: number; y: number } => {
    const v = transformBlockChildPoint(p, basePoint, t);
    return { x: v.x, y: v.y };
  };
  return contexts.map((ctx): AnnotScaleContext => {
    switch (ctx.kind) {
      case "text":
        return {
          ...ctx,
          insertion: pt2(ctx.insertion),
          ...(ctx.alignment ? { alignment: pt2(ctx.alignment) } : {}),
          rotation: ctx.rotation + scalarRotationDeg,
        };
      case "mtext":
      case "mtext_attribute":
      case "attribute":
      case "tolerance":
        return {
          ...ctx,
          insertion: pt2(ctx.insertion),
          rotation: ctx.rotation + scalarRotationDeg,
        };
      case "hatch":
        return {
          ...ctx,
          patternScale: ctx.patternScale * radiusScale,
          patternAngle: mapDirectionAngleDeg(ctx.patternAngle, t),
        };
      default:
        // dimension / mleader / leader / blockref — no positional field a
        // TEXT/MTEXT/HATCH leaf adapter consults (blockref annotative INSERTs
        // are resolved in `blockFromEntity`, not through this leaf path).
        return ctx;
    }
  });
}

/**
 * Spreadable `{ annotativeContexts }` override for a transformed TEXT/MTEXT/
 * HATCH clone — world-space per-scale contexts, or `{}` (no override, the
 * original empty array survives via `...child`) for a non-annotative child.
 * Thin wrapper over {@link transformChildAnnotativeContexts} so all three
 * leaf cases share one call shape.
 */
export function annotativeContextsOverride(
  child: { annotativeContexts?: ReadonlyArray<AnnotScaleContext> },
  basePoint: Vec3,
  t: BlockInsertTransform,
  scalarRotationDeg: number,
  radiusScale: number,
): { annotativeContexts?: AnnotScaleContext[] } {
  const transformed = transformChildAnnotativeContexts(
    child.annotativeContexts ?? [],
    basePoint,
    t,
    scalarRotationDeg,
    radiusScale,
  );
  return transformed ? { annotativeContexts: transformed } : {};
}
